import { createServerColumnHelper, useServerDataTable } from '../shared-compat/DataTable/hooks';
import {
  type CompanyManagementItem,
  type CompanyManagementUserPermissions,
  compareManagementPermissions,
  getLegacyPortalApi,
  makeCompanyManagementPermissions
} from '../legacy-portal-api/legacy-api';
import React, { useMemo, useState } from 'react';
import { DataTable } from '../shared-compat/DataTable/DataTable';
import { Button } from 'react-bootstrap';
const api = getLegacyPortalApi();
const helper = createServerColumnHelper<CompanyManagementItem>();
const ch = helper.accessor;
const display = helper.display;

export const makeCompanyColumns = (perms: CompanyManagementUserPermissions) => {
  const canAdminister = (perms.IsAdministrator || perms.IsGodMode || perms.UserType === 'Owner') && !perms.IsDemoUser;
  return [
    ch('CompanyName', 'string', {
      header: 'Company Name',
      size: 200,
      enableColumnFilter: true,
      cell: (info) => {
        if (info.row.original.CompanyType === 'Partner') {
          return info.getValue() + ' (Partner)';
        }
        return info.getValue();
      }
    }),
    display({
      header: 'Edit Company',
      maxSize: 50,
      enableColumnFilter: true,
      cell: (info) => {
        return (
          <Button
            disabled={!(canAdminister || perms.CanEditCompanies)}
            style={{ width: '100%' }}
            variant='info'
            onClick={() => window.CompaniesEdit(info.row.original.CompanyID)}
          >
            Edit Company
          </Button>
        );
      }
    }),
    display({
      header: 'Add Sites',
      enableColumnFilter: true,
      maxSize: 50,
      cell: (info) => {
        const row = info.row.original;
        const isEnabled = (canAdminister || perms.CanAddSites) && row.CompanyType !== 'Partner';
        return (
          <Button style={{ width: '100%' }} disabled={!isEnabled} variant='warning' onClick={() => window.CompaniesAddSite(info.row.original.CompanyID)}>
            Add Sites
          </Button>
        );
      }
    }),
    display({
      header: 'Edit Sites',
      maxSize: 50,
      enableColumnFilter: true,
      cell: (info) => {
        const row = info.row.original;
        const isEnabled = (canAdminister || perms.CanEditSites) && row.CompanyType !== 'Partner' && row.HasSites;
        return (
          <Button style={{ width: '100%' }} disabled={!isEnabled} variant='info' onClick={() => window.CompaniesEditSite(info.row.original.CompanyID)}>
            Edit Sites
          </Button>
        );
      }
    }),
    display({
      header: 'Add Users',
      maxSize: 50,
      enableColumnFilter: true,
      cell: (info) => {
        return (
          <Button
            style={{ width: '100%' }}
            disabled={!(canAdminister || perms.CanAddUsers)}
            variant='warning'
            onClick={() => window.CompaniesAddUsers(info.row.original.CompanyID)}
          >
            Add Users
          </Button>
        );
      }
    }),
    display({
      header: 'Edit Users',
      maxSize: 50,
      enableColumnFilter: true,
      cell: (info) => {
        const row = info.row.original;
        const isEnabled = row.HasUsers && (canAdminister || perms.CanEditUsers);
        return (
          <Button style={{ width: '100%' }} disabled={!isEnabled} variant='info' onClick={() => window.CompaniesEditUsers(info.row.original.CompanyID)}>
            Edit Users
          </Button>
        );
      }
    })
  ] as const;
};

declare global {
  interface Window {
    reloadCompanyManagementTable: () => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CompaniesEdit: (companyID: string) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CompaniesAddSite: (companyID: string) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CompaniesEditSite: (companyID: string) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CompaniesAddUsers: (companyID: string) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CompaniesEditUsers: (companyID: string) => void;
  }
}
window.reloadCompanyManagementTable = () => {};
export function CompanyManagementTable() {
  const [userPermissions, setUserPermissions] = useState<CompanyManagementUserPermissions>({
    IsAdministrator: false,
    IsDemoUser: false,
    IsGodMode: false,
    CanAddSites: false,
    CanEditSites: false,
    CanAddUsers: false,
    CanEditUsers: false,
    CanEditCompanies: false,
    UserType: 'Customer'
  });
  const columns = useMemo(() => makeCompanyColumns(userPermissions), [userPermissions]);
  const table = useServerDataTable<CompanyManagementItem>({
    queryKey: ['CompanyManagementTable'],
    columns: columns,
    enableColumnFilters: false,
    enableGlobalSearchBar: true,
    initialPageSize: 20,
    getRows: async (p) => {
      const result = await api.getCompanyManagementList(p.limit, p.skip, p.globalFilter ?? '');
      const perms = makeCompanyManagementPermissions(result);
      if (!compareManagementPermissions(userPermissions, perms)) {
        setUserPermissions(perms);
      }
      return {
        count: result.Count,
        data: result.Companies,
        options: [] as unknown[]
      };
    }
  });
  window.reloadCompanyManagementTable = table.refetch;
  return (
    <>
      <DataTable table={table} />
    </>
  );
}
